/**
 * The contact lens job dialog.
 *
 * The contact lens pathway beside glasses. Three steps: prescription -> supply & care -> order &
 * billing. The lens and its powers are not chosen here: they come from a contact lens prescription
 * issued in the prescribing module (LensPrescribingStore), copied onto the job. Dispensing decides
 * what is ordered and how it is paid for - the supply, care products, delivery and the fitting fee.
 * So the happy path is: Continue (the current CL Rx already chosen), Continue, Create invoice & send.
 *
 * Hard lenses (RGP, ortho-k, scleral) are made to order by a lab: the order follows the glasses lab
 * statuses, and each lens bills against the design's inventory SKU.
 *
 * Shares the glasses dialog's shell and styling, and saves through the same LensDispensingStore (as
 * `type: "contacts"`), so both pathways show up together on the tile and the job list.
 */
app.controller("lensContactsModalController", [
	"$scope",
	"$mdDialog",
	"$timeout",
	"$animate",
	"$element",
	"LensDispensingStore",
	"LensPrescribingStore",
	"CurrentPatient",
	"Session",
	"LensStatusDialog",
	"toast",
	"existingJob",
	"jobPatient",
	function ($scope, $mdDialog, $timeout, $animate, $element, Store, Rx, CurrentPatient, Session, LensStatusDialog, toast, existingJob, jobPatient) {
		$scope.S = Store;
		$scope.R = Rx;
		$scope.CP = CurrentPatient;

		// See the glasses dialog: steps switch instantly and fade themselves in, without ngAnimate.
		$animate.enabled(angular.element($element[0].querySelector(".lens-shell")), false);

		const patient = jobPatient || CurrentPatient.patient || {};
		$scope.patient = patient;
		$scope.patientName = [patient.firstName, patient.lastName].filter(Boolean).join(" ") || "Patient";
		$scope.initials = ((patient.firstName || "P").charAt(0) + (patient.lastName || "").charAt(0)).toUpperCase();
		$scope.practiceName = (Session.practice && Session.practice.name) || "Your practice";

		$scope.steps = [
			{ key: "rx", label: "Prescription", icon: "fa-file-text-o" },
			{ key: "supply", label: "Supply & care", icon: "fa-cubes" },
			{ key: "billing", label: "Order & billing", icon: "fa-credit-card" },
		];
		const SUPPLY = 1;
		const BILLING = 2;

		// A reorder, or a job made from a prescription, arrives as an unsaved draft, so it is new even
		// though it comes pre-filled.
		const isNew = !existingJob || existingJob.status === "DRAFT";
		$scope.job = existingJob ? angular.copy(existingJob) : Store.newJob("contacts");
		const job = $scope.job;

		const failMessage = (resp, fallback) => toast.create({ timeout: 6000, message: Store.errorMessage(resp, fallback), className: "alert-danger", dismissible: true });

		$scope.locked = Store.isLocked(job);
		$scope.visited = { rx: true };
		$scope.dirty = false;
		$timeout(markClean);
		$scope.isReorder = !!(job.order && job.order.reorderOf);

		// ------------------------------------------------------------------
		// Navigation
		// ------------------------------------------------------------------
		// A job already supplied opens on its billing; one arriving with its prescription, on the supply.
		const supplied = () => job.supply.boxes.od + job.supply.boxes.os > 0;
		$scope.current = existingJob && (job.invoice || supplied()) ? BILLING : existingJob && job.product ? SUPPLY : 0;
		if ($scope.current === BILLING) $scope.steps.forEach((s) => ($scope.visited[s.key] = true));

		$scope.go = function (i) {
			if (i < 0 || i >= $scope.steps.length) return;
			$scope.current = i;
			$scope.visited[$scope.steps[i].key] = true;
			const body = $element[0].querySelector(".settings-pane-body");
			if (body) body.scrollTop = 0;
		};
		$scope.next = () => $scope.go($scope.current + 1);
		$scope.back = () => $scope.go($scope.current - 1);

		$scope.stepDone = function (key) {
			switch (key) {
				case "rx": return $scope.clValid() && ($scope.locked || !$scope.rxExpired());
				case "supply": return $scope.supplyValid() && (!!$scope.visited.billing || !!job.invoice);
				case "billing": return !!job.invoice;
			}
			return false;
		};

		$scope.progress = function () {
			const done = $scope.steps.filter((s) => $scope.stepDone(s.key)).length;
			return Math.round((done / $scope.steps.length) * 100);
		};

		$scope.stepSummary = function (key) {
			switch (key) {
				case "rx": return job.product ? `${Rx.title(job)} · R ${$scope.noLens("od") ? "none" : Store.fmtPower(job.cl.od.sph)} L ${$scope.noLens("os") ? "none" : Store.fmtPower(job.cl.os.sph)}` : "Choose a prescription";
				case "supply":
					if (!job.product || !$scope.supplyValid()) return $scope.isCustom() ? "Lenses, care" : "Boxes, solution, delivery";
					return $scope.isCustom() ? `${job.supply.boxes.od + job.supply.boxes.os} lenses · made to order` : `${job.supply.months} months · ${job.supply.boxes.od + job.supply.boxes.os} boxes`;
				case "billing": return job.invoice ? "Invoice sent" : "Invoice & order";
			}
		};

		$scope.stepPrice = function (key) {
			const t = $scope.totals();
			if (key === "rx") return t.fittingPrice || null;
			if (key === "supply") return job.product ? t.lensPrice + t.carePrice + t.deliveryPrice : null;
			return null;
		};

		$scope.totals = () => Store.totals(job);

		// Changed since it was opened or last saved, against a snapshot - so a default filled in while
		// loading, or an edit put back, doesn't count as a change.
		let cleanJob = null;
		function markClean() {
			cleanJob = angular.toJson(job);
			$scope.dirty = false;
		}
		$scope.$watch("job", () => ($scope.dirty = cleanJob !== null && angular.toJson(job) !== cleanJob), true);

		// ------------------------------------------------------------------
		// Step 1 - prescription (chosen, not written) and the fitting fee
		// ------------------------------------------------------------------
		$scope.eyes = [{ key: "od", label: "Right", sub: "OD" }, { key: "os", label: "Left", sub: "OS" }];

		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);

		// The patient's contact lens prescriptions that are issued and in date, newest first.
		$scope.prescriptions = () => Rx.usable(patient.id, "contacts");
		$scope.prescription = () => Rx.find(patient.id, job.rxId);
		$scope.rxStatus = (p) => Rx.statusOf(patient.id, p);

		$scope.choosePrescription = function (p) {
			if ($scope.locked) return;
			const changed = job.rxId !== p.id;
			Rx.applyToJob(job, p);
			if (changed) prepareForLens();
		};

		// Supplying contact lenses on an expired Rx needs a new one - only a job not yet sent can change.
		$scope.rxExpired = function () {
			if (!job.cl.expires) return false;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			return new Date(job.cl.expires) < today;
		};

		// What dispensing decides once it knows the lens: a supply of it, care products that suit it,
		// how it reaches the patient and what the fitting costs.
		function prepareForLens() {
			const product = job.product;
			resetBoxes();
			// Keep only the care products that suit this kind of lens, and start reusables off with some.
			const allowed = $scope.careOptions().map((c) => c.id);
			job.supply.care = job.supply.care.filter((id) => allowed.indexOf(id) !== -1);
			if (!job.supply.care.length && product.modality !== "daily") job.supply.care = product.custom ? ["rgp", "case"] : ["mps", "case"];
			// Custom lenses are fitted and handed over in the practice.
			if (product.custom) job.supply.delivery = "practice";
			// A made-to-order lens is priced here, not when it was prescribed: start from any price an older
			// prescription carried.
			if (product.madeToOrder) {
				if (!isNum(job.supply.lensPrice) && product.price > 0) job.supply.lensPrice = product.price;
				if (!isNum(job.supply.leadDays) && product.leadDays > 0) job.supply.leadDays = product.leadDays;
			}
			if (!isNew) return;
			job.fitting.fee = defaultFee();
			// Made-to-order lenses can't go back on the shelf, so take a deposit up front.
			if (product.custom && job.billing.paymentOption === "full") job.billing.paymentOption = "deposit";
		}

		// The first job from a prescription pays for the fitting that wrote it; a repeat supply doesn't.
		function defaultFee() {
			const earlier = Store.getJobs(patient.id).some((j) => j.rxId === job.rxId && j.id !== job.id && j.status !== "DRAFT");
			if (earlier || $scope.isReorder) return "none";
			if (job.product.custom) return "custom";
			return job.product.design !== "spherical" ? "specialty" : "initial";
		}

		$scope.isToric = () => !!job.product && job.product.design === "toric";
		$scope.isMultifocal = () => !!job.product && job.product.design === "multifocal";
		$scope.isCustom = () => !!job.product && !!job.product.custom;

		// The prescription was checked when it was issued; the job only needs one with powers for each eye
		// that wears a lens.
		$scope.clValid = () => !!job.product && Store.lensEyes(job).length > 0 && Store.lensEyes(job).every((k) => isNum(job.cl[k].sph));

		// Each eye's lens - the left can have its own - and whether an eye wears none.
		$scope.lensOf = (eye) => Store.lensFor(job, eye);
		$scope.noLens = (eye) => !!(job.cl[eye] && job.cl[eye].noLens);
		$scope.lensName = (lens) => (lens ? `${lens.brand || ""} ${lens.name || ""}`.trim() : "");

		$scope.fmtEye = function (e) {
			if (e && e.noLens) return "No lens";
			if (!e || !isNum(e.sph)) return "-";
			let s = Store.fmtPower(e.sph);
			if (isNum(e.cyl) && e.cyl !== 0) s += ` / ${Store.fmtPower(e.cyl)} × ${e.axis || "?"}`;
			if (e.add) s += `  Add ${typeof e.add === "number" ? Store.fmtPower(e.add) : e.add}`;
			return s;
		};
		$scope.fmtFit = function (e) {
			if (!e || e.noLens || !e.bc) return "";
			if (!$scope.isCustom()) return `BC ${Number(e.bc).toFixed(1)} · DIA ${Number(e.dia).toFixed(1)}`;
			return `BC ${Number(e.bc).toFixed(2)} · DIA ${Number(e.dia).toFixed(1)} · ${e.material}${e.colour && e.colour !== "Clear" ? " " + e.colour.toLowerCase() : ""}`;
		};

		$scope.designLabel = (key) => (Store.CL_DESIGNS.find((d) => d.key === key) || {}).label || key;
		$scope.modalityLabel = (key) => Store.modalityMeta(key).label;
		$scope.wearLabel = (key) => ({ daily: "Daily wear", part: "Part-time / sport", extended: "Extended (overnight)", overnight: "Overnight (ortho-K)" }[key] || "Daily wear");

		$scope.setFitting = function (key, value) {
			if ($scope.locked) return;
			job.fitting[key] = value;
		};

		// ------------------------------------------------------------------
		// Step 2 - supply & care
		// ------------------------------------------------------------------
		// Each eye by its own lens's box; none for an eye that wears no lens.
		function resetBoxes() {
			const n = (eye) => ($scope.noLens(eye) ? 0 : Store.boxesFor(Store.lensFor(job, eye), job.supply.months));
			job.supply.boxes = { od: n("od"), os: n("os") };
		}

		$scope.setMonths = function (months) {
			if ($scope.locked) return;
			job.supply.months = months;
			resetBoxes();
		};

		$scope.bump = function (eye, by) {
			if ($scope.locked || $scope.noLens(eye)) return;
			// Custom lenses: one, or one and a spare.
			const max = $scope.isCustom() ? 2 : 24;
			job.supply.boxes[eye] = Math.max(0, Math.min(max, (job.supply.boxes[eye] || 0) + by));
		};

		// A made-to-order lens needs its price before it can be invoiced.
		$scope.isMadeToOrder = () => !!job.product && !!job.product.madeToOrder;
		$scope.needsLensPrice = () => $scope.isMadeToOrder() && !(Store.lensPriceFor(job) > 0) && !isNum(job.supply.lensPrice);
		$scope.lensPrice = () => Store.lensPriceFor(job);

		$scope.supplyValid = () => !!job.product && job.supply.boxes.od + job.supply.boxes.os > 0 && !$scope.needsLensPrice();

		$scope.supplyDays = () => Store.supplyDays(job);
		$scope.boxDays = function (eye) {
			const lens = Store.lensFor(job, eye || "od");
			return lens ? lens.pack * Store.modalityMeta(lens.modality).days : 0;
		};
		// The boxes per eye don't match - fine for one-eye wear, worth a look otherwise.
		$scope.uneven = () => job.supply.boxes.od !== job.supply.boxes.os;

		// Daily wearers need nothing reusable; soft and hard lenses each have their own solutions.
		$scope.careOptions = function () {
			const p = job.product;
			if (!p) return Store.CL_CARE.filter((c) => !c.rigid);
			if (p.custom) return Store.CL_CARE.filter((c) => !c.soft);
			return p.modality === "daily" ? Store.CL_CARE.filter((c) => !c.reusable) : Store.CL_CARE.filter((c) => !c.rigid);
		};

		$scope.toggleCare = function (id) {
			if ($scope.locked) return;
			const list = job.supply.care;
			const i = list.indexOf(id);
			if (i >= 0) list.splice(i, 1);
			else {
				// One cleaning system at a time - peroxide and multi-purpose solution don't mix.
				if (id === "mps" || id === "peroxide") {
					const other = list.indexOf(id === "mps" ? "peroxide" : "mps");
					if (other >= 0) list.splice(other, 1);
				}
				list.push(id);
			}
		};

		$scope.deliveryDisabled = (d) => d.key === "home" && $scope.isCustom();

		$scope.setDelivery = function (key) {
			if ($scope.locked || $scope.deliveryDisabled({ key: key })) return;
			job.supply.delivery = key;
			// Shipped orders are paid up front - there is no collection to pay at.
			if (key === "home" && job.billing.paymentOption === "collection") job.billing.paymentOption = "full";
		};

		// When to remind the patient: a fortnight before the supply runs out, counted from about when
		// the order arrives.
		$scope.reorderDate = () => Store.addDays($scope.eta(), Math.max($scope.supplyDays() - 14, 0));

		// ------------------------------------------------------------------
		// Step 3 - order & billing
		// ------------------------------------------------------------------
		$scope.paymentOptions = [
			{ key: "full", label: "Pay in full now", icon: "fa-check-circle" },
			{ key: "deposit", label: "50% deposit now", icon: "fa-adjust" },
			{ key: "collection", label: "Pay on collection", icon: "fa-clock-o" },
		];
		$scope.paymentDisabled = (p) => p.key === "collection" && job.supply.delivery === "home";
		$scope.setPayment = function (p) {
			if (!$scope.paymentDisabled(p)) job.billing.paymentOption = p.key;
		};


		$scope.eta = () => Store.addDays(job.created, Store.leadDaysFor(job) + (job.supply.delivery === "home" ? 2 : 1));
		$scope.supplier = () => Store.supplierFor(job);

		// Items added by hand to go on this invoice - an eye examination with the lenses, say - so
		// the visit doesn't need a second invoice. The name searches the practice's Xero billing items
		// and its inventory, like the invoice dialog; a new name can also just be typed.
		$scope.newItem = { name: "", price: null, code: null, pickedName: null };
		$scope.searchItems = (term) => Store.searchBillingItems(term);
		$scope.itemLabel = Store.billingItemLabel;
		$scope.pickItem = function (item) {
			$scope.newItem.name = item.name;
			if (isNum(item.unit_price)) $scope.newItem.price = Number(item.unit_price);
			$scope.newItem.code = item.code || null;
			$scope.newItem.pickedName = item.name;
		};
		$scope.canAddItems = () => !$scope.locked && !job.invoice;
		$scope.addCustomItem = function () {
			const name = ($scope.newItem.name || "").trim();
			if (!$scope.canAddItems() || !name || !(Number($scope.newItem.price) >= 0)) return;
			if (!job.customItems) job.customItems = [];
			// A picked item keeps its Xero code, so the invoice bills the existing item - unless the
			// name was changed after picking, which makes it a new one.
			const code = name === $scope.newItem.pickedName ? $scope.newItem.code : null;
			job.customItems.push({ name: name, price: Number($scope.newItem.price), code: code });
			$scope.newItem = { name: "", price: null, code: null, pickedName: null };
		};
		$scope.removeCustomItem = function (index) {
			if (!$scope.canAddItems()) return;
			job.customItems.splice(index, 1);
		};

		// Built fresh each time, so cache them per change of the job (see the glasses dialog).
		let cachedLines = [];
		$scope.billingLines = () => cachedLines;
		// Once invoiced, the lines are the invoice's own; before, they are priced from the settings.
		const refreshLines = () => (cachedLines = Store.jobLines(job));
		$scope.$watch("job", refreshLines, true);

		// Boxes on hand of each eye's prescription, for a lens from inventory; their variations go with the
		// job, and the server takes the boxes off stock once the invoice is approved - or back-orders what
		// isn't there, which is most contact lenses.
		Store.loadContactLensStock().catch(angular.noop);
		$scope.stockOf = (eye) => Store.stockFor(Store.lensFor(job, eye), job.cl && job.cl[eye]);
		$scope.stockNote = function (eye) {
			const v = $scope.stockOf(eye);
			const wanted = job.supply.boxes[eye];
			if (!v) return "no such box in inventory - order in";
			if (!v.stock) return "none in stock - ordered in for the patient";
			return v.stock < wanted ? `only ${v.stock} in stock - the rest ordered in` : `${v.stock} in stock`;
		};

		// The practice's fitting fees, care products and delivery, and their prices.
		$scope.settingsState = { loading: true, error: null };
		Store.loadSettings()
			.then(refreshLines)
			.catch((resp) => ($scope.settingsState.error = Store.errorMessage(resp, "The practice's dispensing extras could not be loaded.")))
			.finally(() => ($scope.settingsState.loading = false));

		$scope.missing = function () {
			return $scope.steps.slice(0, BILLING).filter((s) => (s.key === "supply" ? !$scope.supplyValid() : !$scope.stepDone(s.key)));
		};

		// What stops the invoice being created, said on the action bar as the one thing to do - null once
		// nothing does. A string, so the template can watch it.
		const XERO_NEEDS = {
			checking: "Checking Xero\u2026",
			login: "Log in to Xero first",
			contact: "Choose the patient's Xero contact",
			archived: "Restore the Xero contact first",
			conflict: "Choose the Xero organisation",
			unreachable: "Xero unreachable - retry",
		};
		$scope.invoiceBlocker = function () {
			const missing = $scope.missing();
			if (missing.length) return `Finish ${missing[0].label.toLowerCase()} first`;
			if ($scope.xero.enabled && !$scope.xero.ready && !$scope.xero.skipped) return XERO_NEEDS[$scope.xero.step] || XERO_NEEDS.checking;
			return null;
		};

		// Takes the user to it: the unfinished step, or the Xero panel (its contact search, if that's what's needed).
		$scope.fixBlocker = function () {
			const missing = $scope.missing();
			if (missing.length) return $scope.go($scope.steps.indexOf(missing[0]));
			const panel = $element[0].querySelector(".lens-xero");
			if (!panel) return;
			panel.scrollIntoView({ behavior: "smooth", block: "center" });
			const search = panel.querySelector(".lens-xero-search input");
			if (search) search.focus();
		};

		$scope.canInvoice = () => !$scope.invoiceBlocker() && !$scope.sending;

		// A practice billing through Xero raises the invoice there; lens-xero-invoicing checks the login
		// and the patient's contact, and says when both are in place - or that the user chose to invoice
		// without Xero, kept in CatTrax to raise in Xero later.
		const practiceFeatures = (Session.practice && Session.practice.practiceFeatures) || {};
		$scope.xero = { enabled: !!practiceFeatures.billingFeature, ready: false };

		$scope.sending = false;
		$scope.saving = false;

		// The server's copy replaces the dialog's, keeping the object the template is bound to.
		function adopt(saved) {
			angular.copy(saved, job);
			$scope.locked = Store.isLocked(job);
			$timeout(markClean);
			return job;
		}

		// The invoice is created - in Xero, approved, where the practice bills there - and the dialog stays
		// open for the payment.
		$scope.createInvoice = function () {
			if (!$scope.canInvoice()) return;
			$scope.sending = true;
			// The patient's Xero contact is linked, or made, as the invoice is raised.
			const keepOutOfXero = $scope.xero.enabled && !!$scope.xero.skipped;
			const contactReady = $scope.xero.enabled && !keepOutOfXero && $scope.xero.prepare ? $scope.xero.prepare() : null;
			const invoice = () => Store.invoiceJob(patient.id, job, null, false, keepOutOfXero);
			(contactReady ? contactReady.then(invoice) : invoice())
				.then(function (saved) {
					if (saved.$orderPdfFailed) {
						delete saved.$orderPdfFailed;
						failMessage(null, "The job is invoiced, but its order PDF couldn't be made. Marking the order sent makes it again.");
					}
					adopt(saved);
					const notice = Store.invoicedNotice(saved, $scope.patientName, false);
					if (notice) toast.create({ timeout: 6000, message: notice.message, className: notice.className, dismissible: true });
				})
				.catch(function (resp) {
					// Logged out of Xero since the check: the panel goes back to its login step.
					if (resp && resp.status === 401 && $scope.xero.check) $scope.xero.check();
					failMessage(resp, "The job could not be invoiced.");
				})
				.finally(() => ($scope.sending = false));
		};

		// A job kept in CatTrax, raised in Xero from its invoice card.
		$scope.raisedInXero = (saved) => adopt(saved);

		// Paid: the dialog's work is done.
		$scope.paid = function (saved) {
			$mdDialog.hide(saved);
		};

		// Closing an invoiced job without (more) payment. It is locked, so there is nothing to save.
		$scope.finishInvoiced = function () {
			$mdDialog.hide(job);
		};

		$scope.canSaveQuote = () => !!job.product && !!job.rxId && !$scope.saving;

		$scope.saveQuote = function () {
			if (!$scope.canSaveQuote()) return;
			$scope.saving = true;
			Store.saveJob(patient.id, job)
				.then((saved) => $mdDialog.hide(saved))
				.catch((resp) => failMessage(resp, "The quote could not be saved."))
				.finally(() => ($scope.saving = false));
		};


		// Marking the order sent from here once the job is invoiced - the same step as Mark order sent on the job
		// tile: it makes the order PDF and saves it to the patient's documents. Moving the job on isn't an edit,
		// so the lock doesn't stop it.
		$scope.canSendOrder = () => !!job.id && (Store.nextActionFor(job) || {}).next === "ORDERED";
		$scope.sendingOrder = false;
		$scope.sendOrder = function () {
			if (!$scope.canSendOrder() || $scope.sendingOrder) return;
			const action = Store.nextActionFor(job);
			const sentTo = $scope.supplier();
			$scope.sendingOrder = true;
			LensStatusDialog.show(job, patient, action, true)
				.then(
					(result) => Store.advanceJob(patient.id, result.job || job, { reference: result.reference || null }).then(function (sent) {
						adopt(sent);
						toast.create({
							timeout: 6000, className: "alert-success", dismissible: true,
							message: `Order marked sent to ${job.order.lab || sentTo}. The order PDF is in ${patient.firstName || "the patient"}'s documents.`,
						});
					}),
					// Cancelled in the dialog.
					() => null
				)
				.catch((resp) => failMessage(resp, "The order could not be sent."))
				.finally(() => ($scope.sendingOrder = false));
		};

		// The order PDF, saved to the patient's files when the order was sent.
		$scope.hasOrderDocument = () => Store.hasOrderDocument(job);
		$scope.viewOrderDocument = function () {
			Store.orderDocument(patient.id, job).then(
				function (blob) {
					const url = URL.createObjectURL(blob);
					window.open(url, "_blank");
					setTimeout(() => URL.revokeObjectURL(url), 60000);
				},
				(resp) => failMessage(resp, "The order PDF could not be opened.")
			);
		};

		// The patient's prescriptions. A new job starts from their current contact lens Rx, if they have
		// one; a job made from a prescription already has it, and only needs its supply.
		$scope.rxState = { loading: true, error: null };
		Rx.load(patient.id)
			.then(function () {
				if (!isNew || $scope.locked) return;
				const latestRx = Rx.usable(patient.id, "contacts")[0];
				if (!job.rxId && latestRx) $scope.choosePrescription(latestRx);
				else if (job.product && !$scope.isReorder && !supplied()) prepareForLens();
				$timeout(markClean);
			})
			.catch((resp) => ($scope.rxState.error = Store.errorMessage(resp, "The patient's prescriptions could not be loaded.")))
			.finally(() => ($scope.rxState.loading = false));

		// ------------------------------------------------------------------
		// Close
		// ------------------------------------------------------------------
		$scope.confirmClose = false;
		// See the glasses dialog: set from here, not from inside the prompt's ng-if child scope.
		$scope.keepEditing = () => ($scope.confirmClose = false);
		$scope.close = function () {
			if (($scope.dirty || (isNew && $scope.isReorder)) && job.product && !$scope.locked && !job.invoice) {
				$scope.confirmClose = true;
				return;
			}
			$mdDialog.cancel();
		};
		$scope.discard = () => $mdDialog.cancel();
	},
]);
